import { useState } from 'react';
import useTripStore from '../../context/TripStore';
import MobileNavigationView from './MobileNavigationView';

/**
 * Mobile itinerary view with bottom sheet over map
 */
function MobileItineraryView() {
  const itinerary = useTripStore((state) => state.itinerary);
  const places = useTripStore((state) => state.places ?? []);
  const navigation = useTripStore((state) => state.navigation);
  const setActivePlan = useTripStore((state) => state.setActivePlan);
  const startNavigation = useTripStore((state) => state.startNavigation);
  const setCurrentStep = useTripStore((state) => state.setCurrentStep);

  const [isExpanded, setIsExpanded] = useState(false);

  const isNavigating = navigation?.isActive;
  const activePlanKey = itinerary?.activePlan || 'a';
  const activePlan = activePlanKey === 'a' ? itinerary?.planA : itinerary?.planB;

  // Show navigation view when trip is started
  if (isNavigating) {
    return <MobileNavigationView />;
  }

  // Create a map of locked times for quick lookup
  const lockedTimesMap = {};
  places.forEach(place => {
    if (place.lockedTime) {
      lockedTimesMap[place.name] = place.lockedTime;
    }
  });

  const handleStartTrip = () => {
    startNavigation();
  };

  const handleBack = () => {
    setCurrentStep('list');
  };

  // Note: handleNewTrip available for future use
  // const handleNewTrip = () => {
  //   if (window.confirm('Start a new trip? This will clear your current itinerary.')) {
  //     reset();
  //     setCurrentStep('list');
  //   }
  // };

  const sheetHeight = isExpanded ? '85vh' : '55vh';

  return (
    <>
      {/* Map is visible behind - rendered by App.js BackgroundMap */}

      {/* Bottom sheet */}
      <div
        className="fixed bottom-0 left-0 right-0 z-40 bg-white rounded-t-3xl shadow-2xl transition-all duration-300 ease-out flex flex-col"
        style={{ height: sheetHeight, maxHeight: sheetHeight }}
      >
        {/* Drag handle */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex justify-center pt-3 pb-2 w-full"
          aria-label={isExpanded ? 'Collapse sheet' : 'Expand sheet'}
        >
          <div className="w-10 h-1 bg-gray-300 rounded-full" />
        </button>

        {/* Header with plan toggle */}
        <div className="px-4 pb-3 border-b border-gray-100">
          <div className="flex items-center justify-between mb-3">
            <h1 className="text-lg font-semibold text-gray-900">Your Itinerary</h1>
            <button
              onClick={handleBack}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Edit
            </button>
          </div>

          {/* Plan toggle tabs */}
          <div className="flex gap-2">
            <button
              onClick={() => setActivePlan('a')}
              disabled={!itinerary?.planA}
              className={`flex-1 py-2 px-3 text-sm font-medium rounded-lg transition-colors ${
                activePlanKey === 'a'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              } disabled:opacity-50`}
            >
              {itinerary?.planA?.title || 'Realistic'}
            </button>
            <button
              onClick={() => setActivePlan('b')}
              disabled={!itinerary?.planB}
              className={`flex-1 py-2 px-3 text-sm font-medium rounded-lg transition-colors ${
                activePlanKey === 'b'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              } disabled:opacity-50`}
            >
              {itinerary?.planB?.title || 'Ambitious'}
            </button>
          </div>
        </div>

        {/* Scrollable timeline */}
        <div className="flex-1 overflow-y-auto px-4 py-3">
          {activePlan?.summary && (
            <p className="text-sm text-gray-600 mb-4 pb-3 border-b border-gray-100">
              {activePlan.summary}
            </p>
          )}

          {activePlan?.stops?.length > 0 ? (
            <div className="space-y-3">
              {activePlan.stops.map((stop, index) => {
                const isLocked = lockedTimesMap[stop.name] !== undefined;
                return (
                  <div key={index} className="flex gap-3">
                    {/* Timeline indicator */}
                    <div className="flex flex-col items-center">
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium ${
                        isLocked
                          ? 'bg-amber-100 text-amber-700 border-2 border-amber-300'
                          : 'bg-blue-100 text-blue-700'
                      }`}>
                        {index + 1}
                      </div>
                      {index < activePlan.stops.length - 1 && (
                        <div className="w-0.5 flex-1 bg-gray-200 my-1 min-h-[40px]" />
                      )}
                    </div>

                    {/* Stop card */}
                    <div className="flex-1 pb-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-gray-900 text-sm truncate">
                            {stop.name}
                            {isLocked && (
                              <span className="ml-1 text-amber-600 text-xs">locked</span>
                            )}
                          </h3>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {stop.arrival_time} - {stop.departure_time}
                            <span className="mx-1">·</span>
                            {stop.duration_minutes} min
                          </p>
                        </div>
                      </div>

                      {stop.duration_reasoning && (
                        <p className="text-xs text-gray-400 mt-1 line-clamp-2">
                          {stop.duration_reasoning}
                        </p>
                      )}

                      {/* Transport to next */}
                      {stop.transport_to_next && index < activePlan.stops.length - 1 && (
                        <div className="mt-2 flex items-center gap-1.5 text-xs text-gray-500">
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                          </svg>
                          <span>
                            {stop.transport_to_next.mode} · {stop.transport_to_next.duration_minutes} min
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <p className="text-sm">No stops in this plan</p>
            </div>
          )}

          {/* Cuts section */}
          {activePlan?.cuts?.length > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                Didn't fit
              </h3>
              <div className="space-y-1">
                {activePlan.cuts.map((cut, index) => (
                  <p key={index} className="text-xs text-gray-400">
                    <span className="line-through">{cut.name}</span>
                    <span className="mx-1">-</span>
                    {cut.reason}
                  </p>
                ))}
              </div>
            </div>
          )}

          {/* Extra padding for fixed button */}
          <div className="h-20" />
        </div>

        {/* Fixed start button */}
        <div className="absolute bottom-0 left-0 right-0 p-4 bg-white border-t border-gray-100" style={{ paddingBottom: 'max(16px, env(safe-area-inset-bottom))' }}>
          <button
            onClick={handleStartTrip}
            className="w-full py-3.5 bg-blue-600 text-white font-semibold rounded-xl active:bg-blue-700 transition-colors"
          >
            Start Trip
          </button>
        </div>
      </div>
    </>
  );
}

export default MobileItineraryView;
